<template>
	<section
		v-if="showSection"
		class="bg-gradient-to-b md:bg-gradient-to-t from-[#F3F7FD]"
		data-path="sectionsCounties"
	>
		<AdaptiveContainer class="flex justify-between items-center">
			<BaseHeading
				type="h5"
				class="my-9 col-span-full relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
			>
				{{ $t("countyJobs.otherCounties") }}
			</BaseHeading>
			<div class="text-sm hidden sm:flex">
				<NuxtLink :to="`/${$i18n.locale}/locations`" class="text-sm">
					{{ $t("countyJobs.allCounties") }}
					<span class="material-icons-outlined pl-2 text-base">arrow_forward</span>
				</NuxtLink>
			</div>
		</AdaptiveContainer>

		<AdaptiveContainer>
			<div v-if="loading" class="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-8">
				<div
					v-for="index in numberOfCountiesToShow"
					:key="index"
					class="h-[168px] rounded-big border border-dj-gray-light bg-dj-gray-minimal animate-pulse"
					:class="{ 'hidden sm:block': index > 4 }"
				></div>
			</div>

			<div v-else class="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-8">
				<CardLocationJobs
					v-for="(item, index) in countyCards"
					:key="item.id"
					:class="{ 'hidden sm:block': index >= 4 }"
					:href="item.href"
					:name="item.name"
					:jobs-count="item.jobsCount"
					:categories="item.categories"
				/>
			</div>
		</AdaptiveContainer>

		<div class="sm:hidden mb-6">
			<NuxtLink
				:to="`/${$i18n.locale}/locations`"
				class="w-full flex justify-center items-center bg-dj-blue/5 text-sm text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10 max-w-[320px] mx-auto mb-4 p-3 rounded-normal"
			>
				{{ $t("countyJobs.allCounties") }}
				<span class="material-icons-outlined pl-2">arrow_forward</span>
			</NuxtLink>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { constructApiUrl } from "~/composables/helpers";
import type { CountiesApiResponse } from "~/types/county";

type CountyCard = {
	id: number;
	name: string;
	href: string;
	jobsCount: number;
	categories: string[];
};

const { locale } = useI18n();
const regionCode = useRegion.getCurrentRegion().code.toLowerCase();
const numberOfCountiesToShow = 8;

const { data: fetchedCounties, pending: loading } = await useAsyncData(
	`home-counties-grid-${locale.value}-${regionCode}`,
	async () => {
		const url = constructApiUrl("/counties");
		url.searchParams.append("region", regionCode);

		const response = await $fetch<CountiesApiResponse>(url.href);
		return response?.data ?? [];
	},
	{ default: () => [] }
);

const countyCards = computed<CountyCard[]>(() => {
	const mapped = (fetchedCounties.value ?? []).map((county) => {
		const countyName =
			county.name[locale.value] ?? county.name["en"] ?? county.name["hu"] ?? county.name["ro"] ?? "";

		const countySlug =
			county.slug[locale.value] ?? county.slug["en"] ?? county.slug["hu"] ?? county.slug["ro"] ?? "";

		const topCategories = county.locations
			.flatMap((location) => location.categories.map((category) => category.name))
			.filter((value, index, source) => source.indexOf(value) === index)
			.slice(0, 4);

		return {
			id: county.id,
			name: countyName,
			href: `/${locale.value}/jobs/county/${countySlug}`,
			jobsCount: county.jobs_count,
			categories: topCategories,
		};
	});

	return mapped.slice(0, numberOfCountiesToShow);
});

const showSection = computed(() => loading.value || countyCards.value.length > 0);
</script>
